// Calls to outside numbers (FE-22 subset). The dialer accepts an E.164 destination only while the
// server reports `calling.pstn.outbound` enabled for this person; otherwise it shows why, in plain
// words, and offers no way to try. The server still validates every call (line, destination,
// spend, concurrency, kill switch, 40/day warming cap); its refusal codes are mapped in lib/errorCopy.ts.
import type { Capability } from '@bcio-voice/contracts';
import { copyForCode } from '../lib/errorCopy.ts';
import type { Diagnostic } from '../lib/userCopy.ts';

export type PstnReadiness =
  | { ready: true }
  | { ready: false; reason: string; diagnostic: Diagnostic | null };

export interface NumberWarmingInfo {
  enabled: boolean;
  timezone: string;
  local_day: string;
  outbound_attempts: number;
  daily_limit: number;
  remaining_attempts: number;
  exhausted: boolean;
  resets_at: string;
  resets_at_label?: string;
}

export interface OutboundNumberCandidate {
  id: string;
  e164?: string;
  label?: string;
  status?: string;
  capabilities: { voice_out: boolean };
  assigned_to_user?: boolean;
  assigned_user_count?: number;
  outbound_usable?: boolean;
  is_spare?: boolean;
  spare_priority?: number | null;
  warming?: NumberWarmingInfo | null;
}

export interface CallingFromContext {
  isAdmin?: boolean;
}

export interface CallingFromOption<N> {
  number: N;
  usable: boolean;
  why: string | null;
  authorized: boolean;
  exhausted: boolean;
  isSpare: boolean;
  quotaLabel: string | null;
}

/** Fallback copy by status when the server's reason code has no specific sentence. */
const BY_STATUS = {
  disabled: 'Calls to outside numbers are turned off for this workspace.',
  unconfigured: 'Calling outside numbers isn’t set up yet.',
  unimplemented: 'Calls to outside numbers aren’t available in this version.',
  degraded: 'Calls to outside numbers are having problems right now, so they’re paused.',
} as const;
const byStatus = (status: string): string => (status in BY_STATUS ? BY_STATUS[status as keyof typeof BY_STATUS] : BY_STATUS.disabled);

export function pstnReadiness(cap: Capability | null | undefined): PstnReadiness {
  if (!cap) return { ready: false, reason: 'Still checking whether calls to outside numbers are available.', diagnostic: null };
  const diagnostic = cap.reason_code || cap.detail ? { code: cap.reason_code, detail: cap.detail } : null;
  if (!cap.allowed) return { ready: false, reason: copyForCode('pstn_permission_required', BY_STATUS.disabled), diagnostic };
  if (cap.status === 'enabled') return { ready: true };
  const known = cap.reason_code?.startsWith('pstn_') ? copyForCode(cap.reason_code, '') : '';
  return { ready: false, reason: known || byStatus(cap.status), diagnostic };
}

/**
 * True only when the active workspace session holds `owner` or `admin` role and is not in read-only `view_as` mode.
 * `numbers:read_all` or `calls:read_all` permissions alone never grant arbitrary outbound line selection.
 */
export function isSessionOutboundAdmin(
  session: {
    tenant?: { id: string } | null;
    memberships?: readonly { tenant_id: string; role_ids: readonly string[] }[];
    view_as?: unknown;
  } | null | undefined,
): boolean {
  if (!session || session.view_as) return false;
  const memberships = session.memberships ?? [];
  const activeTenantId = session.tenant?.id;
  const activeMembership = activeTenantId
    ? memberships.find(m => m.tenant_id === activeTenantId)
    : memberships[0];
  if (!activeMembership) return false;
  return activeMembership.role_ids.some(r => r === 'owner' || r === 'admin');
}

export function formatWarmingResetLabel(warming: NumberWarmingInfo | null | undefined): string {
  if (!warming) return 'midnight local time';
  if (warming.resets_at_label && warming.resets_at_label.trim()) return warming.resets_at_label.trim();
  if (warming.resets_at) return warming.resets_at;
  return 'midnight local time';
}

export function formatWarmingQuotaLabel(number: OutboundNumberCandidate): string | null {
  const w = number.warming;
  if (!w || !w.enabled) return null;
  const rolePrefix = number.is_spare ? 'spare: ' : number.assigned_to_user === false ? 'unassigned: ' : '';
  if (w.exhausted || w.outbound_attempts >= w.daily_limit) {
    return `${rolePrefix}${w.outbound_attempts}/${w.daily_limit} limit reached — resets ${formatWarmingResetLabel(w)}`;
  }
  return `${rolePrefix}${w.outbound_attempts}/${w.daily_limit} today (${w.remaining_attempts} left)`;
}

export function numberWarmingInlineText(number: OutboundNumberCandidate | null | undefined): string | null {
  const w = number?.warming;
  if (!w || !w.enabled) return null;
  return `${w.outbound_attempts}/${w.daily_limit} today (${w.remaining_attempts} left)`;
}

export function numberWarmingDailyLimit(number: OutboundNumberCandidate | null | undefined): number {
  return number?.warming?.daily_limit ?? 40;
}

function compareSpares<N extends OutboundNumberCandidate>(a: N, b: N): number {
  const pa = a.spare_priority ?? 999999;
  const pb = b.spare_priority ?? 999999;
  if (pa !== pb) return pa - pb;
  const ea = a.e164 ?? '';
  const eb = b.e164 ?? '';
  if (ea !== eb) return ea.localeCompare(eb);
  return a.id.localeCompare(b.id);
}

/** Numbers this person may choose for an outside call, plus the rest with the reason they can't. */
export function callingFromOptions<N extends OutboundNumberCandidate>(
  numbers: readonly N[],
  context?: CallingFromContext,
): CallingFromOption<N>[] {
  const isAdmin = context?.isAdmin === true;
  return numbers.map(number => {
    const isSpare = number.is_spare === true;
    const warming = number.warming ?? null;
    const exhausted = warming?.enabled === true && (warming.exhausted === true || warming.outbound_attempts >= warming.daily_limit);
    const assigned = number.assigned_to_user !== false;
    const authorizedByRole = isAdmin || isSpare || number.assigned_to_user === true || (number.assigned_to_user === undefined && number.outbound_usable !== false);
    const authorized = number.outbound_usable === true || (authorizedByRole && (number.outbound_usable !== false || isAdmin || isSpare || number.assigned_to_user === true || exhausted));
    const active = !number.status || number.status === 'active';
    const usable = Boolean(number.capabilities.voice_out && active && authorized && !exhausted);
    const quotaLabel = formatWarmingQuotaLabel(number);
    const why = !authorized && !assigned
      ? 'not assigned to you'
      : !authorized
      ? 'not assigned to you'
      : !number.capabilities.voice_out
      ? 'can’t make outgoing calls'
      : !active
      ? (number.status ?? 'inactive')
      : exhausted
      ? `${warming!.outbound_attempts}/${warming!.daily_limit} warming limit reached — resets ${formatWarmingResetLabel(warming)}`
      : null;
    return { number, usable, why, authorized, exhausted, isSpare, quotaLabel };
  });
}

/**
 * Selects the caller's default outgoing line:
 * 1. Non-exhausted personal assigned line (`assigned_to_user === true`, `assigned_user_count === 1`, `!is_spare`).
 * 2. Non-exhausted shared assigned line (`assigned_to_user === true`, `!is_spare`).
 * 3. First non-exhausted shared spare (`is_spare === true`) in deterministic configured order (`spare_priority ASC, e164 ASC, id ASC`).
 * Never infers ownership from labels alone and never selects another rep's unassigned personal line as default.
 */
export function defaultOutboundNumberId<N extends OutboundNumberCandidate>(
  numbers: readonly N[],
  context?: CallingFromContext,
): string | null {
  const options = callingFromOptions(numbers, context);
  const usableAssignedNonSpare = options
    .filter(o => o.usable && o.number.assigned_to_user === true && !o.isSpare)
    .map(o => o.number);
  const personal = usableAssignedNonSpare.find(n => n.assigned_user_count === 1);
  if (personal) return personal.id;
  if (usableAssignedNonSpare.length > 0) {
    const sortedShared = [...usableAssignedNonSpare].sort((a, b) => (a.assigned_user_count ?? 999) - (b.assigned_user_count ?? 999));
    return sortedShared[0]?.id ?? null;
  }
  const usableSpares = options
    .filter(o => o.usable && o.isSpare)
    .map(o => o.number)
    .sort(compareSpares);
  if (usableSpares.length > 0) {
    return usableSpares[0]!.id;
  }
  return null;
}

export interface ResolvedOutboundSelection<N extends OutboundNumberCandidate> {
  options: CallingFromOption<N>[];
  usableOptions: CallingFromOption<N>[];
  effectiveFromId: string | null;
  fromNumber: N | null;
  rotatedFromNumber: N | null;
  exhaustedBlockReason: string | null;
}

/**
 * Resolves the effective outbound line selection, automatically rotating from an exhausted personal/selected
 * line (`40/40`) to the first available shared spare in `spare_priority` order, or returning a clear
 * next-reset refusal reason if all authorized lines are exhausted.
 */
export function resolveOutboundLineSelection<N extends OutboundNumberCandidate>(
  numbers: readonly N[],
  selectedFromId: string | null,
  context?: CallingFromContext,
): ResolvedOutboundSelection<N> {
  const options = callingFromOptions(numbers, context);
  const usableOptions = options.filter(o => o.usable);
  const defaultFromId = defaultOutboundNumberId(numbers, context);

  const selectedOption = selectedFromId ? options.find(o => o.number.id === selectedFromId) ?? null : null;
  const exhaustedPersonalOption = options.find(
    o => o.exhausted && o.number.assigned_to_user === true && !o.isSpare && o.number.assigned_user_count === 1,
  ) ?? options.find(o => o.exhausted && o.number.assigned_to_user === true && !o.isSpare) ?? null;

  let effectiveFromId: string | null = null;
  let rotatedFromNumber: N | null = null;

  if (selectedOption && selectedOption.usable) {
    effectiveFromId = selectedOption.number.id;
    if (exhaustedPersonalOption && selectedOption.isSpare && selectedOption.number.id !== exhaustedPersonalOption.number.id) {
      rotatedFromNumber = exhaustedPersonalOption.number;
    }
  } else if (selectedOption && selectedOption.exhausted) {
    const firstSpare = usableOptions
      .filter(o => o.isSpare)
      .map(o => o.number)
      .sort(compareSpares)[0] ?? null;
    effectiveFromId = firstSpare?.id ?? defaultFromId;
    if (effectiveFromId) {
      rotatedFromNumber = selectedOption.number;
    }
  } else {
    effectiveFromId = defaultFromId;
    if (effectiveFromId && exhaustedPersonalOption) {
      const effectiveOpt = options.find(o => o.number.id === effectiveFromId);
      if (effectiveOpt?.isSpare) {
        rotatedFromNumber = exhaustedPersonalOption.number;
      }
    }
  }

  const fromNumber = effectiveFromId ? (options.find(o => o.number.id === effectiveFromId)?.number ?? null) : null;

  let exhaustedBlockReason: string | null = null;
  if (usableOptions.length === 0) {
    const exhaustedAuthorized = options.filter(o => o.authorized && o.exhausted && o.number.capabilities.voice_out);
    if (exhaustedAuthorized.length > 0) {
      const sortedByReset = [...exhaustedAuthorized].sort((a, b) => {
        const ta = a.number.warming?.resets_at ? Date.parse(a.number.warming.resets_at) : Number.MAX_SAFE_INTEGER;
        const tb = b.number.warming?.resets_at ? Date.parse(b.number.warming.resets_at) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
      const earliest = sortedByReset[0]!.number.warming;
      const limit = earliest?.daily_limit ?? 40;
      const resetText = formatWarmingResetLabel(earliest);
      exhaustedBlockReason = `All available outbound lines have reached the ${limit}-call daily warming limit. Next reset: ${resetText}.`;
    }
  }

  return {
    options,
    usableOptions,
    effectiveFromId,
    fromNumber,
    rotatedFromNumber,
    exhaustedBlockReason,
  };
}
